import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchProgrammaticPages } from '../lib/api';
import { programmaticPagePath } from '../lib/programmaticPageUrl';

const GROUP_LABELS = {
    pillar: 'Investment & area guides',
    area_guide: 'Area guides',
    howto: 'How-to guides',
    city_config: 'City property guides',
    city_budget: 'Budget guides',
    comparison: 'Developer comparisons',
    nearby_landmark: 'Nearby landmarks',
    new_launch: 'New launch projects',
    locality_guide: 'Locality guides',
};

function groupLabel(pageType) {
    return GROUP_LABELS[pageType] || pageType.replace(/_/g, ' ');
}

export default function FooterSeoLinksSection() {
    const [pages, setPages] = useState([]);

    useEffect(() => {
        fetchProgrammaticPages({ status: 'active', limit: 500 })
            .then(setPages)
            .catch(() => setPages([]));
    }, []);

    const grouped = useMemo(() => {
        const map = new Map();
        const seen = new Set();

        for (const page of pages) {
            const path = programmaticPagePath(page);
            if (!path || seen.has(path)) continue;
            seen.add(path);
            const key = page.page_type || 'other';
            if (!map.has(key)) map.set(key, []);
            map.set(key, [...map.get(key), { path, title: page.title || page.h1 || page.slug, page }]);
        }

        for (const [, items] of map) {
            items.sort((a, b) => a.title.localeCompare(b.title));
        }

        const order = ['pillar', 'area_guide', 'howto', 'city_config', 'city_budget', 'comparison', 'nearby_landmark', 'new_launch', 'locality_guide'];
        return [...map.entries()].sort((a, b) => {
            const ai = order.indexOf(a[0]);
            const bi = order.indexOf(b[0]);
            return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
        });
    }, [pages]);

    if (!grouped.length) return null;

    return (
        <section
            className="bg-[#1B4332] text-white pt-12 sm:pt-14 pb-10 sm:pb-12 border-b border-white/10"
            aria-labelledby="footer-seo-links-heading"
            data-testid="footer-seo-links"
        >
            <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10">
                <h2
                    id="footer-seo-links-heading"
                    className="text-[13px] font-semibold mb-2 tracking-wide"
                >
                    Explore properties & guides
                </h2>
                <p className="text-[13px] text-white/65 mb-8 leading-relaxed">
                    Browse all guides, city pages, and comparisons across Zircon Estates.
                </p>

                <div className="space-y-8">
                    {grouped.map(([pageType, links]) => (
                        <div key={pageType}>
                            <h3 className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#E07B39] mb-3">
                                {groupLabel(pageType)}
                            </h3>
                            <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2.5">
                                {links.map(({ path, title }) => (
                                    <li key={path}>
                                        <Link
                                            to={path}
                                            className="text-[14px] text-white/65 hover:text-white transition-colors leading-snug"
                                        >
                                            {title}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                <div className="mt-8 pt-6 border-t border-white/10">
                    <Link to="/guides" className="text-[14px] font-semibold text-white/65 hover:text-white transition-colors">
                        View all guides hub →
                    </Link>
                </div>
            </div>
        </section>
    );
}
